// @flow

import React, { memo, lazy } from 'react';
import Attribution from 'app/common/react/components/PinRep/components/Footer/Attribution/Attribution';
import AttributionAvatar from 'app/common/react/components/PinRep/components/Footer/Attribution/AttributionAvatar';
import BoardActivityPinRepComments from 'app/common/react/components/BoardActivities/BoardActivityPinRepComments';
import CarouselEllipsis from 'app/common/react/components/PinRep/components/Footer/CarouselEllipsis';
import enablePointerEvents from 'app/common/react/components/PinRep/utils/enablePointerEvents';
import getAttributionProps from 'app/common/react/components/PinRep/utils/getAttributionProps';
import ImageOnlyAttributionText from 'app/common/react/components/PinRep/components/Footer/Attribution/ImageOnlyAttributionText';
import OverflowMenu from 'app/common/react/components/PinRep/components/OverflowMenu';
import PinStats from 'app/common/react/components/PinRep/components/Footer/PinStats';
import SafeSuspense from 'app/packages/safe-suspense';
import Source from 'app/common/react/components/PinRep/components/Footer/Source';
import Title from 'app/common/react/components/PinRep/components/Footer/Title';
import UserNote from 'app/common/react/components/PinRep/components/Footer/UserNote';
import { type AttributionType } from 'app/common/react/components/PinRep/utils/getAttributionType';
import { type DetailedBoard } from 'app/packages/pinterest-flow-types/board';
import { type Pin } from 'app/common/react/components/PinRep/types';
import {
  DEFAULT_TRANSITION_DURATION_MS,
  type TransitionState,
} from 'app/common/react/components/PinRep/Hooks/useTransitionManager';
import {
  type FeedbackData,
  type FeedbackType,
} from 'app/common/react/components/PinRep/feedback-types';
import { Box, Text } from 'gestalt';

const BoardActivityPinRepReactions = lazy(() =>
  import(/* webpackChunkName: "BoardActivityPinRepReactionsCommon" */ 'app/common/react/components/BoardActivities/BoardActivityPinRepReactions'),
);

const styles = {
  container: {
    padding: '8px 6px 16px',
  },
  groupBoardContainer: {
    padding: '8px 6px 8px',
  },
  imageOnlyAttribution: {
    hidden: {
      opacity: 0,
      transition: `opacity ${DEFAULT_TRANSITION_DURATION_MS}ms ease`,
    },
    visible: {
      opacity: 1,
    },
  },
};

export type Props = {|
  pin: Pin,
  board: ?DetailedBoard,

  // What type of attribution should be shown, if any?
  attributionType: AttributionType,

  // Block click events from triggering, if they're being handled elsewhere
  blockClickEvents?: boolean,

  feedbackData?: FeedbackData,

  // Different Pin types offer different feedback options/behaviors.
  feedbackType: FeedbackType,

  // The users' Pin hover state.
  hoverTransitionState: ?TransitionState,

  // Used to force a hover experience. See PinRepContainer for more context.
  setForcePrimary: (boolean) => void,

  // Pin title is determined in PinRepConfiguration, as it takes the surface
  // into account.
  pinTitle: string,

  /*** Imperative props handed down from PinRepConfiguration: ***/

  // Determines whether a promoted pin is from a user with an account. Used to determine whether or not to show AvatarHoverCard.
  isDarkPromoter?: boolean,
  showAvatarAndNameOnLeft: boolean,
  showBoardActivityComments: boolean,
  showBoardActivityReply: boolean,
  showCarouselEllipsis: boolean,
  showGroupBoardAvatar: boolean,
  showOverflowMenu: boolean,
  showPinStats: boolean,
  showReactions: boolean,
  showSource: boolean,
  singleLineMaxLength: number,
  titleLines: 1 | 2 | 3,
  useLegoPadding: boolean,
  userNote: ?string,
|};

// The Footer is used to display information at the bottom of the PinRep. This
// information is typically only actionable in the form of links.
export function Footer({
  attributionType,
  blockClickEvents,
  feedbackData,
  feedbackType,
  hoverTransitionState,
  isDarkPromoter,
  pin,
  board,
  pinTitle,
  setForcePrimary,
  showAvatarAndNameOnLeft,
  showBoardActivityComments,
  showBoardActivityReply,
  showCarouselEllipsis,
  showGroupBoardAvatar,
  showOverflowMenu,
  showPinStats,
  showReactions,
  showSource,
  singleLineMaxLength,
  titleLines,
  useLegoPadding,
  userNote,
}: Props) {
  const { board_activity: boardActivity, id: pinId } = pin;
  const hasTitleOrSource = pinTitle || showSource;
  const hasTextContent = hasTitleOrSource || userNote;
  const { avatarUrl, attributionNameLink, name } = getAttributionProps(pin);

  // Build attribution.
  let attribution = null;
  if (attributionType === 'default') {
    attribution = (
      <Attribution
        feedbackData={feedbackData}
        feedbackType={feedbackType}
        pin={pin}
        pinTitle={pinTitle}
        setForcePrimary={setForcePrimary}
        isDarkPromoter={isDarkPromoter}
      />
    );
  } else if (attributionType === 'image-only-credit') {
    attribution = (
      <Box
        dangerouslySetInlineStyle={{
          __style:
            styles.imageOnlyAttribution[hoverTransitionState === 'active' ? 'visible' : 'hidden'],
        }}
      >
        <ImageOnlyAttributionText pin={pin} feedbackType={feedbackType} />
      </Box>
    );
  }

  const content = (
    <Box
      dangerouslySetInlineStyle={{
        __style:
          board && board.is_collaborative && useLegoPadding
            ? styles.groupBoardContainer
            : styles.container,
      }}
    >
      {/* Carousel */}
      {showCarouselEllipsis && (
        <Box
          dangerouslySetInlineStyle={{
            __style:
              hasTitleOrSource || showGroupBoardAvatar || attributionType !== 'none'
                ? { marginBottom: '6px' }
                : {},
          }}
          display="flex"
          justifyContent="center"
        >
          <Box flex="none">
            <CarouselEllipsis pinId={pinId} />
          </Box>
        </Box>
      )}

      {showPinStats && (
        <Box
          dangerouslySetInlineStyle={{ __style: { marginBottom: '6px' } }}
          flex="grow"
          width="100%"
        >
          <PinStats pin={pin} />
        </Box>
      )}

      <Box display="flex">
        <Box flex="grow">
          {/* Title, Source, User Note */}
          {hasTextContent && (
            <Box
              dangerouslySetInlineStyle={{
                __style:
                  attributionType === 'none' && !showReactions ? {} : { marginBottom: '6px' },
              }}
            >
              <Title
                numberOfLines={titleLines}
                pin={pin}
                pinTitle={pinTitle}
                singleLineMaxLength={singleLineMaxLength}
              />
              {showSource && <Source pin={pin} singleLineMaxLength={singleLineMaxLength} />}
              {userNote && (
                <Box marginTop={hasTitleOrSource ? 1 : 0}>
                  <UserNote
                    pin={pin}
                    showGroupBoardAvatar={showGroupBoardAvatar}
                    singleLineMaxLength={singleLineMaxLength}
                    userNote={userNote}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Attribution and OverflowMenu */}
          <Box alignItems="center" display="flex">
            <Box flex="grow">{attribution}</Box>
            {showOverflowMenu && (
              <Box flex="none">
                <OverflowMenu
                  feedbackData={feedbackData}
                  feedbackType={feedbackType}
                  pin={pin}
                  pinTitle={pinTitle}
                  setForcePrimary={setForcePrimary}
                  transparentBackground
                />
              </Box>
            )}
          </Box>

          {/* Group Board Avatar and Name */}
          {showAvatarAndNameOnLeft && (
            <Box alignItems="center" display="flex" marginTop={1} marginBottom={1}>
              <AttributionAvatar
                attributionNameLink={attributionNameLink}
                avatarUrl={avatarUrl}
                name={name}
              />
              <Box marginLeft={2}>
                <Text size="sm">{name}</Text>
              </Box>
            </Box>
          )}

          {/* Reactions */}
          {showReactions && (
            <Box position="relative" data-test-id="board-activity-reactions" minHeight={16}>
              <SafeSuspense>
                <BoardActivityPinRepReactions
                  pinId={pin.id}
                  board={board}
                  activityId={(boardActivity && boardActivity.id) || pin.id}
                  showReply={showBoardActivityReply}
                  // If we have comment data, we'll show them inline so this module shouldn't show comments
                  canShowComments={!showBoardActivityComments}
                />
              </SafeSuspense>
            </Box>
          )}
        </Box>

        {/* Group Board Avatar (placed to right of all other footer content) */}
        {!showAvatarAndNameOnLeft &&
          showGroupBoardAvatar && (
            <Box flex="none" marginRight={1}>
              <AttributionAvatar
                attributionNameLink={attributionNameLink}
                avatarUrl={avatarUrl}
                name={name}
              />
            </Box>
          )}
      </Box>

      {/* Comments */}
      {showBoardActivityComments && <BoardActivityPinRepComments pin={pin} />}
    </Box>
  );

  return blockClickEvents ? (
    <div style={{ pointerEvents: 'none' }}>{content}</div>
  ) : (
    enablePointerEvents(content)
  );
}

export default memo<Props>(Footer);
